import { BicycleRoute, BMNCity, Community, MapEvent, Project } from '@/interfaces';
import PopoverService from '@/services/popover.service';
import HelperService from '@/services/helper.service';
import RouteService from './route.service';
import bicycleRoutes from '@/assets/data/fietsroutes.json';
import { Observable, Observer } from 'rxjs';
import { Vue } from 'vue-property-decorator';
import DataService from './data.service';

export default class MapService extends Vue {
  private communities: Community[];
  private smallPopoverVisible = false;
  private bigPopoverVisible = false;
  private popoverService = new PopoverService();
  private routeService = new RouteService();
  private helperService = new HelperService();
  private dataService = new DataService();

  private previousItem = {id: null};

  private items: any;

  public addUiElements(map: any, routes: BicycleRoute[], locations: BMNCity[], communities: Community[],
                       projects: Project[]): any {
    const observable = Observable.create((observer: Observer<any>) => {
      this.communities = communities;
      this.routeService.addRoutes(map, routes)
      .subscribe((data: any) => {
        this.routeService.addLocations(map, locations);
        this.routeService.addLabelsForProjects(map, projects);
        observer.next(data);
        observer.complete();
      });
    });
    return observable;
  }

  public setRouteEvents(routes: BicycleRoute[], map: any): void {
    this.setMapEvents(routes, map, 'route');
  }

  public setLocationEvents(locations: BMNCity[], map: any): void {
    this.setMapEvents(locations, map, 'location');
  }

  public setCommunityEvents(communities: Community[], map: any): void {
    this.setMapEvents(communities, map, 'community');
  }

  public setProjectEvents(projects: Project[], map: any): void {
    this.setMapEvents(projects, map, 'project');
  }

  public toggleItems(visible: boolean, items: any[], map: any): void {
    if (!items) {
      return;
    }
    this.unpaint(map, this.previousItem, items);
    this.removePopover();
    const visibility = visible ? 'visible' : 'none';
    items.forEach((item) => {
      if (item.id.endsWith('-sfr')) {
        return;
      }
      map.setLayoutProperty(`${item.id}`, 'visibility', visibility);
    });
  }

  public stageTwoPopover(map: any, comId: string) {
    if (!this.communities) {
      return;
    }
    const item = this.dataService.getCommunityById(comId, this.communities);
    this.removePopover();
    this.bigPopoverVisible = true;
    this.popoverService.showPopover({lng: item.coordinate[0], lat: item.coordinate[1]}, item, map, true, false);
    this.previousItem.id = comId;
  }

  private setMapEvents(items: any, map: any, type: string): void {
    this.items = items;
    if (type === 'location') {
      this.items = this.mapProjects(items);
      return;
    }
    if (type === 'project') {
      this.items = this.mapProjects(items);
    }
    this.items.forEach(item => {
      map.on('mouseover', `${item.id}`, (event: MapEvent) => {
        map.getCanvas().style.cursor = 'pointer';
        this.paint(map, item, items, type);
        if (this.helperService.isMobile() || this.helperService.isTablet() || this.bigPopoverVisible) {
          return;
        }
        if (this.previousItem.id !== item.id) {
          this.unpaint(map, this.previousItem, this.items);
        }
        this.removePopover();
        this.hoverEvent(event, item, map, this.items, type);
        this.previousItem.id = item.id;
      });
      map.on('click', `${item.id}`, (event: MapEvent) => {
        window.location.hash = '';
        this.removePopover();
        if (this.previousItem.id !== item.id) {
          this.unpaint(map, this.previousItem, this.items);
        }
        const lngLat = item.isContainer ? {lng: item.coordinate[0], lat: item.coordinate[1]} : event.lngLat;
        this.popoverService.showPopover(lngLat, item, map, true, false, item.isContainer);
        this.bigPopoverVisible = true;
        this.previousItem.id = item.id;
      });
      map.on('mouseleave', `${item.id}`, () => {
        map.getCanvas().style.cursor = 'grab';
        if (document.getElementsByClassName('mapboxgl-popup')[0]
            && document.getElementsByClassName('mapboxgl-popup')[0].matches(':hover')) {
          return;
        }
        this.unpaint(map, item, this.items);
        if (this.helperService.isMobile() || this.helperService.isTablet() || this.bigPopoverVisible) {
          return;
        }
        window.location.hash = '';
        if (this.smallPopoverVisible) {
          this.removePopover();
        }

        if (item.id === this.previousItem.id) {
          return;
        }
      });
    });
    map.on('click', e => {
      if (map.queryRenderedFeatures(e.point).length === 0) {
        this.removePopover();
        this.unpaint(map, this.previousItem, this.items);
      }
    });
    map.on('dragstart', () => {
      if (this.helperService.isMobile()) {
        return;
      }
      this.unpaint(map, this.previousItem, items);
      this.removePopover();
    });
    map.on('zoom', () => {
      this.unpaint(map, this.previousItem, items);
      this.removePopover();
    });
  }

  private hoverEvent(event: MapEvent, item: any, map: any, items: any, type: string): void {
    this.popoverService.showPopover(event.lngLat, item, map, true, true);
    this.smallPopoverVisible = true;
    document.getElementsByClassName('mapboxgl-popup')[0].addEventListener('mouseleave', () => {
      this.removePopover();
      this.unpaint(map, item, items);
    });
  }

  private paint(map: any, item: any, items: any, type: string): void {
    const itemForPaintProperties = this.mapItem(item, items);
    itemForPaintProperties.forEach(i => {
      this.setPaintProperties(map, i.id, this.getPaintProperties(type, true, map, false));
    });
  }

  private unpaint(map: any, item: any, items: any): void {
    if (!item.id) {
      return;
    }
    const type = this.getType(item);
    let properties;
    if (item.id.startsWith('route') && bicycleRoutes.find(route => route.id === item.id).status === 'planned') {
      properties = this.getPaintProperties(type, false, map, true);
    } else {
      properties = this.getPaintProperties(type, false, map, false);
    }
    if (type === 'project') {
      item = this.mapItem(item, items);
      item.forEach(i => {
        this.setPaintProperties(map, i.id, properties);
      });
      return;
    }
    this.setPaintProperties(map, item.id, properties);
  }

  private getPaintProperties(type: string, show: boolean, map: any, planned: boolean) {
    if (show) {
      return this.getShowPaintProperties(type, map);
    }
    return this.getHidePaintProperties(type, planned);
  }

  private setPaintProperties(map: any, layerId: string,
                             properties: Array<{type: string, value: string | number | any[]}>) {
    if (!properties || !map.getLayer(layerId)) {
      return;
    }
    properties.forEach((prop: {type: string, value: string | number | any[]}) => {
      map.setPaintProperty(map.getLayer(layerId).id, prop.type, prop.value);
    });
  }

  private getShowPaintProperties(type: string, map: any): Array<{type: string, value: string | number | any[]}> {
    if (type === 'route') {
      return [{type: 'line-color', value: '#0091ab'}, {type: 'line-width', value: this.getLineZoom(map, type)}];
    } else if (type === 'community') {
      return [{type: 'fill-color', value: '#0091ab'}, {type: 'fill-opacity', value: 0.6}];
    } else if (type === 'project') {
      return [{type: 'line-color', value: '#018ba8'}, {type: 'line-width', value: this.getLineZoom(map, type)}];
    }
  }

  private getHidePaintProperties(type: string, planned: boolean)
    : Array<{type: string, value: string | number | any[]}> {
    if (type === 'route') {
      const color = planned ? '#ffe500' : '#d9535a';
      return [{type: 'line-color', value: color}, {type: 'line-width', value: [
        'interpolate',
        ['linear'],
        ['zoom'],
        7.64,
        1,
        9.9,
        8
      ]}];
    } else if (type === 'community') {
      return [{type: 'fill-color', value: '#00B4E9'}, {type: 'fill-opacity', value: 0.4}];
    } else if (type === 'project') {
      return [{ type: 'line-color', value: '#575876'}, {type: 'line-width', value: [
          'interpolate',
          ['linear'],
          ['zoom'],
          7.64,
          2,
          9.9,
          6
        ]
      }];
    }
  }

  private getLineZoom(map: any, type: string): number {
    return (type === 'route') ? this.getLineZoomForRoutes(map) : this.getLineZoomForProjects(map);
  }

  private getLineZoomForRoutes(map: any): number {
    if (map.getZoom() > 9) {
      return 11;
    }
    if (map.getZoom() > 8) {
      return 6;
    }
    return 3;
  }

  private getLineZoomForProjects(map: any): number {
    if (map.getZoom() > 9) {
      return 8;
    }
    if (map.getZoom() > 8) {
      return 6;
    }
    return 3;
  }

  private mapProjects(projects: Project[]) {
    const items = [];
    projects.forEach(project => {
      items.push({id: `${project.id}`});
    });
    return items;
  }

  private mapItem(item: any, items: any): any {
    if (!item.id.startsWith('mirt')) {
      return [item];
    }

    const projectType = item.id.split('-')[0];
    const projects = items.filter(i => {
      return i.id.startsWith(projectType);
    });
    return projects;
  }

  private getType(item: any) {
    if (item.id.startsWith('com')) {
      return 'community';
    }
    if (item.id.startsWith('route')) {
      return 'route';
    }

    return 'project';
  }

  private removePopover() {
    this.popoverService.removePopover();
    this.bigPopoverVisible = false;
    this.smallPopoverVisible = false;
  }
}
